import { useState } from "react";
import {
  useConvex,
  usePaginatedQuery,
  useQuery,
  type ConvexReactClient,
} from "convex/react";
import {
  decodeCaptureChunk,
  type CaptureFrame,
} from "@anthemreactv2/contracts";
import { api } from "@anthemreactv2/backend/labs-api";
import type {
  Doc,
  Id,
} from "@anthemreactv2/backend/convex/_generated/dataModel";
import { SharedAction, fieldText } from "./SharedAction";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Notice, Panel } from "./components/workbench";
import { message } from "./cloud";
import { parseWorkspace, type Workspace } from "./workspace";

export async function readSharedWorkspace(
  client: ConvexReactClient,
  recording: Doc<"labsRecordings">,
): Promise<Workspace> {
  if (recording.state !== "sealed")
    throw new Error("Complete this capture before opening it for review.");
  const frames: CaptureFrame[] = [];
  for (let chunk = 0; chunk < recording.nextChunk; chunk++) {
    const row = await client.query(api.labs.recordings.readChunk, {
      roasteryId: recording.roasteryId,
      recordingId: recording._id,
      chunk,
    });
    if (!row)
      throw new Error(
        "A recording chunk is unavailable. Try again before comparing this curve.",
      );
    frames.push(...decodeCaptureChunk(row.json).frames);
  }
  if (frames.length !== recording.nextSequence)
    throw new Error("The recording is incomplete.");
  return parseWorkspace({
    version: 2,
    source: {
      name: recording.name,
      provenance: recording.provenance,
      samples: frames.map(({ elapsedMs, beanC, environmentC }) => ({
        elapsedMs,
        beanC,
        environmentC,
      })),
    },
    reference: null,
    events: recording.events,
    history: [],
    playheadMs: 0,
  });
}
export function SharedRecordings({
  roasteryId,
  manager,
  operator,
  workspace,
  onOpen,
}: {
  roasteryId: Id<"labsRoasteries">;
  manager: boolean;
  operator: boolean;
  workspace: Workspace | null;
  onOpen: (workspace: Workspace) => void;
}) {
  const client = useConvex(),
    recordings = usePaginatedQuery(
      api.labs.recordings.list,
      { roasteryId },
      { initialNumItems: 25 },
    );
  const machines = useQuery(api.labs.roasteries.machines, { roasteryId });
  const [error, setError] = useState<string | null>(null),
    [busy, setBusy] = useState<string | null>(null);
  return (
    <>
      <Panel
        title="Shared recordings"
        description="Captures and reference versions available to your roastery."
        action={
          workspace &&
          operator && (
            <SharedAction
              label="Share current replay"
              description={`Upload ${workspace.source.name} and its event marks as a sealed copy. The source remains labeled ${workspace.source.provenance}.`}
              fields={[
                {
                  name: "machine",
                  label: "Recorded on machine",
                  options: (machines ?? []).map((row) => ({
                    value: row._id,
                    label: row.name,
                  })),
                },
              ]}
              disabled={!machines?.length}
              submit={async (data, operationKey) => {
                // Portable imports are self-declared data; provenance never proves hardware authenticity.
                const recordingId = await client.mutation(
                  api.labs.recordings.begin,
                  {
                    roasteryId,
                    machineId: fieldText(data, "machine") as Id<"labsMachines">,
                    name: workspace.source.name,
                    provenance: workspace.source.provenance,
                    clientSessionId: operationKey,
                    deviceDescription:
                      workspace.source.provenance === "device"
                        ? "Imported device recording; original setup must be verified"
                        : undefined,
                  },
                );
                const recording = await client.query(api.labs.recordings.get, {
                  roasteryId,
                  recordingId,
                });
                for (
                  let start = recording.nextSequence,
                    chunk = recording.nextChunk;
                  start < workspace.source.samples.length;
                  start += 120, chunk++
                ) {
                  const frames = workspace.source.samples
                    .slice(start, start + 120)
                    .map((sample, index) => ({
                      ...sample,
                      elapsedMs: sample.elapsedMs,
                      sequence: start + index,
                      receivedAt: 0,
                      epoch: 0,
                      quality:
                        sample.beanC === null && sample.environmentC === null
                          ? "missing"
                          : "ok",
                    }));
                  await client.mutation(api.labs.recordings.append, {
                    roasteryId,
                    recordingId,
                    chunk,
                    json: JSON.stringify({ version: 1, frames }),
                  });
                }
                await client.mutation(api.labs.recordings.seal, {
                  roasteryId,
                  recordingId,
                  expectedFrames: workspace.source.samples.length,
                  events: workspace.events.map((event) => ({
                    ...event,
                    elapsedMs: event.elapsedMs,
                  })),
                });
              }}
            />
          )
        }
      >
        {error && <Notice error>{error}</Notice>}
        {recordings.status === "LoadingFirstPage" ? (
          <p role="status">Loading recordings…</p>
        ) : !recordings.results.length ? (
          <p className="py-6 text-sm text-muted-foreground">
            Sync a local hardware capture, or share the current replay.
          </p>
        ) : (
          <div className="divide-y">
            {recordings.results.map((recording) => (
              <div
                className="flex flex-wrap items-center justify-between gap-4 py-4"
                key={recording._id}
              >
                <div>
                  <p className="font-medium">{recording.name}</p>
                  <p className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <Badge variant="outline">{recording.provenance}</Badge>
                    {recording.state === "sealed"
                      ? "Complete"
                      : "Upload in progress"}{" "}
                    · {recording.nextSequence.toLocaleString()} frames
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy !== null || recording.state !== "sealed"}
                    onClick={async () => {
                      if (busy) return;
                      setBusy(recording._id);
                      setError(null);
                      try {
                        onOpen(await readSharedWorkspace(client, recording));
                      } catch (cause) {
                        setError(message(cause));
                      } finally {
                        setBusy(null);
                      }
                    }}
                  >
                    {busy === recording._id ? "Opening…" : "Open recording"}
                  </Button>
                  {manager && recording.state === "sealed" && (
                    <SaveSharedProfile recording={recording} />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {recordings.status === "CanLoadMore" && (
          <Button variant="ghost" onClick={() => recordings.loadMore(25)}>
            Show more recordings
          </Button>
        )}
      </Panel>
      <SharedProfiles roasteryId={roasteryId} onOpen={onOpen} />
    </>
  );
}
function SaveSharedProfile({
  recording,
}: {
  recording: Doc<"labsRecordings">;
}) {
  const client = useConvex(),
    profiles = usePaginatedQuery(
      api.labs.recordings.profiles,
      { roasteryId: recording.roasteryId },
      { initialNumItems: 25 },
    );
  return (
    <SharedAction
      label="Save profile version"
      description="Freeze this complete curve and its event marks as a reusable machine reference. Existing versions stay unchanged."
      fields={[
        { name: "name", label: "Profile name", value: recording.name },
        {
          name: "profile",
          label: "Existing profile (optional)",
          optional: true,
          options: profiles.results.map((row) => ({
            value: row._id,
            label: `${row.name} · current v${row.revision}`,
          })),
        },
      ]}
      submit={(data, operationKey) => {
        const profile = profiles.results.find(
          (row) => row._id === fieldText(data, "profile"),
        );
        return client.mutation(api.labs.recordings.saveProfile, {
          roasteryId: recording.roasteryId,
          recordingId: recording._id,
          name: fieldText(data, "name"),
          profileId: profile?._id,
          expectedRevision: profile?.revision ?? 0,
          operationKey,
        });
      }}
    />
  );
}
function SharedProfiles({
  roasteryId,
  onOpen,
}: {
  roasteryId: Id<"labsRoasteries">;
  onOpen: (workspace: Workspace) => void;
}) {
  const profiles = usePaginatedQuery(
    api.labs.recordings.profiles,
    { roasteryId },
    { initialNumItems: 25 },
  );
  const [selected, setSelected] = useState<Id<"labsProfiles"> | null>(null);
  return (
    <Panel
      title="Reference profiles"
      description="Choose a version to review its curve and event marks."
    >
      <div className="flex flex-wrap gap-2">
        {profiles.results.map((profile) => (
          <Button
            key={profile._id}
            variant={profile._id === selected ? "secondary" : "outline"}
            onClick={() => setSelected(profile._id)}
          >
            {profile.name} · v{profile.revision}
          </Button>
        ))}
      </div>
      {profiles.status === "LoadingFirstPage" ? (
        <p role="status">Loading profiles…</p>
      ) : (
        !profiles.results.length && (
          <p className="text-sm text-muted-foreground">
            Save a complete recording as your first reference.
          </p>
        )
      )}
      {profiles.status === "CanLoadMore" && (
        <Button variant="ghost" onClick={() => profiles.loadMore(25)}>
          Show more profiles
        </Button>
      )}
      {selected && (
        <ProfileVersions
          key={selected}
          roasteryId={roasteryId}
          profileId={selected}
          onOpen={onOpen}
        />
      )}
    </Panel>
  );
}
function ProfileVersions({
  roasteryId,
  profileId,
  onOpen,
}: {
  roasteryId: Id<"labsRoasteries">;
  profileId: Id<"labsProfiles">;
  onOpen: (workspace: Workspace) => void;
}) {
  const client = useConvex(),
    versions = usePaginatedQuery(
      api.labs.recordings.versions,
      { roasteryId, profileId },
      { initialNumItems: 25 },
    );
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null);
  return (
    <div className="space-y-3 border-t pt-4">
      {versions.results.map((version) => (
        <Button
          key={version._id}
          variant="ghost"
          className="mr-2"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              const recording = await client.query(api.labs.recordings.get, {
                roasteryId,
                recordingId: version.recordingId,
              });
              const workspace = await readSharedWorkspace(client, recording);
              onOpen({
                ...workspace,
                reference: {
                  ...workspace.source,
                  name: version.name,
                  events: workspace.events,
                  profile: {
                    id: version.profileId,
                    revision: version.revision,
                  },
                },
              });
            } catch (cause) {
              setError(message(cause));
            } finally {
              setBusy(false);
            }
          }}
        >
          Open version {version.revision}
        </Button>
      ))}
      {error && <Notice error>{error}</Notice>}
      {versions.status === "CanLoadMore" && (
        <Button variant="ghost" onClick={() => versions.loadMore(25)}>
          Older versions
        </Button>
      )}
    </div>
  );
}
